import type { GPProblem, RNG } from "../types"
import { ProblemError } from "./errors"

export interface CreateProblemSpec<T> {
  /** Fitness score: higher is better (must be finite). */
  fitness: GPProblem<T>["fitness"]
  /** Return a mutated copy of the genome. */
  mutate: GPProblem<T>["mutate"]
  /** Optional crossover; defaults to identity (returns parents). */
  crossover?: GPProblem<T>["crossover"]
  /**
   * Optional random initializer; if omitted, provide `init` and we will
   * generate via a few mutation steps from that seed.
   */
  createRandom?: GPProblem<T>["createRandom"]
  /** Optional deterministic seed factory used to derive createRandom. */
  init?: () => T
  /** Optional hooks forwarded as-is. */
  isValid?: GPProblem<T>["isValid"]
  repair?: GPProblem<T>["repair"]
  distance?: GPProblem<T>["distance"]
}

/**
 * createProblem: convenience to build a GPProblem with sane defaults.
 *
 * If `createRandom` is not provided, you can supply `init()` and a random
 * genome will be produced by applying 1..3 mutation steps from the seed.
 * If neither is provided, an error is thrown (we cannot generically create T).
 */
export function createProblem<T>(spec: CreateProblemSpec<T>): GPProblem<T> {
  const {
    fitness,
    mutate,
    crossover,
    createRandom,
    init,
    isValid,
    repair,
    distance,
  } = spec

  if (typeof fitness !== "function")
    throw new ProblemError("fitness must be a function", "E_INVALID_FITNESS_FN")
  if (typeof mutate !== "function")
    throw new ProblemError("mutate must be a function", "E_INVALID_MUTATE_FN")

  let cr: (rng: RNG) => T
  if (createRandom) {
    // Delegate to user-supplied initializer (sync or async allowed by engine choice)
    // For the sync engine, callers should provide a sync createRandom.
    cr = (rng) => createRandom(rng) as T
  } else if (init) {
    cr = (rng) => {
      let g = init() as T
      const n = 1 + rng.int(3)
      for (let i = 0; i < n; i++) g = mutate(g, rng) as T
      return g
    }
  } else {
    throw new ProblemError(
      "createProblem requires either createRandom or init to generate genomes",
      "E_MISSING_INIT_RANDOM",
    )
  }

  const cx: GPProblem<T>["crossover"] = crossover ?? ((a, _b) => [a as T])

  const problem: GPProblem<T> = {
    createRandom: (rng, _ctx) => cr(rng),
    fitness,
    mutate,
    crossover: cx,
    isValid,
    repair,
    distance,
  }
  return problem
}
